import { useEffect, useState } from 'react';
import { useClientData } from '@/providers/ClientData.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import useFetchRailzBusinessAccount from '@/hooks/useApi/useFetchRailzBusinessAccount';
import { RailzService } from '@/components/Intergrations/AccountingSoftware/railzServices';
import { INTEGRATIONS } from './integrationsCatalogue';
import { IntegrationCardData, IntegrationCountry } from './types';

function toIntegrationCountry(country: string): IntegrationCountry | undefined {
  return country === 'CA' || country === 'US' ? country : undefined;
}

/**
 * The catalogue visible to the current account: country is a hard filter (a US account never sees
 * Flinks or Inverite, not even locked), permission-gated cards are dropped, and each remaining card
 * gets its state from the account flags plus the accounting software Railz reports as connected.
 *
 * `isLoading` is true until everything the filters depend on has arrived — rendering earlier shows
 * the wrong cards (Country is '' on the first paint) in the wrong state.
 */
export default function useIntegrationCatalogue(): {
  integrations: IntegrationCardData[];
  connectedRailzServices: RailzService[];
  isLoading: boolean;
} {
  const {
    CustomFlinksCredentialsSet,
    CustomPlaidCredentialsSet,
    CustomInveriteCredentialsSet,
    CustomGlobalPaySharedSecret,
    AccountingSoftwareIntegrationEnabled,
  } = useLockedFeatures();

  const { permissions } = usePermissions();
  const [, , , fetchRailzBusinessAccount] = useFetchRailzBusinessAccount({});
  const [connectedRailzServices, setConnectedRailzServices] = useState<RailzService[]>([]);
  const [isRailzResolved, setIsRailzResolved] = useState(false);

  const {
    response: { Country },
  } = useClientData();

  // Railz is only asked about when an ERP card can be shown at all
  const isErpVisible = Boolean(AccountingSoftwareIntegrationEnabled && permissions.ViewAccountingBillsAndInvoices);

  useEffect(() => {
    if (!isErpVisible) return;

    const loadConnectedServices = async () => {
      try {
        const res = await fetchRailzBusinessAccount({});

        if (!res.Success) throw new Error(res.ErrorMessage);

        setConnectedRailzServices(Object.values(res.Accounts).map(({ ServiceName }) => ServiceName));
      } catch (error) {
        // The legacy hook's async passthrough neither checks Success nor logs, so log it here.
        // ERP cards stay unconnected — the connect flow still works from the card.
        console.error(error);
      } finally {
        setIsRailzResolved(true);
      }
    };

    void loadConnectedServices();
  }, [isErpVisible]);

  // Country arrives with the login response, one render after the provider mounts
  const isLoading = Country === '' || (isErpVisible && !isRailzResolved);

  if (isLoading) return { integrations: [], connectedRailzServices, isLoading };

  const country = toIntegrationCountry(Country);

  // 12 static definitions, so this filter+map is cheaper than memoizing it: both provider contexts
  // hand back a new object on every render, which no dependency array can see through
  const integrations = INTEGRATIONS.filter(
    ({ countries, requiresPermission }) =>
      (!country || countries.includes(country)) && (!requiresPermission || permissions[requiresPermission]),
  ).map(({ getState, ...integration }) => ({
    ...integration,
    state: getState({
      CustomFlinksCredentialsSet,
      CustomPlaidCredentialsSet,
      CustomInveriteCredentialsSet,
      CustomGlobalPaySharedSecret,
      AccountingSoftwareIntegrationEnabled,
      connectedRailzServices,
    }),
  }));

  return { integrations, connectedRailzServices, isLoading };
}
